import React, { useMemo } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  mergeClasses,
} from '@fluentui/react-components';
import {
  Add16Regular,
  Delete20Regular,
  Desktop24Regular,
  Edit20Regular,
  Link16Regular,
  MoreHorizontal20Regular,
  Search20Regular,
  Speaker220Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { Consultorio, QueueDisplayScreen, Sector, Sede } from '../../../../models/Entities/Turnero';
import { useOfficesStyles } from '../Offices.Styles';
import {
  DisplayFilterKey,
  LocationFilter,
  countDisplays,
  displayMatchesStatus,
  filterDisplays,
  findById,
  findTestCallMedicoId,
  getSectorRoomIds,
  getSectorSedeId,
} from '../Offices.Functions';
import {
  DisplayActivePill,
  DisplayPairingPill,
  EmptyState,
  FilterPills,
  ListSkeleton,
  LocationFilters,
} from '../OfficesCommon/OfficesCommon.Component';

export type DisplaysFilter = LocationFilter & { status: DisplayFilterKey };

export interface DisplaysTabProps {
  displays: QueueDisplayScreen[];
  sectores: Sector[];
  sedes: Sede[];
  consultorios: Consultorio[];
  isLoading: boolean;
  filter: DisplaysFilter;
  selectedId?: number;
  onFilterChange: (filter: DisplaysFilter) => void;
  onCreate: () => void;
  onPair: () => void;
  onEdit: (display: QueueDisplayScreen) => void;
  onDelete: (display: QueueDisplayScreen) => void;
  onTestCall: (display: QueueDisplayScreen, medicoId: string) => void;
}

const DisplaysTab = ({
  displays,
  sectores,
  sedes,
  consultorios,
  isLoading,
  filter,
  selectedId,
  onFilterChange,
  onCreate,
  onPair,
  onEdit,
  onDelete,
  onTestCall,
}: DisplaysTabProps) => {
  const { t } = useTranslation();
  const styles = useOfficesStyles();

  const located = useMemo(() => filterDisplays(displays, { ...filter, status: 'all' }, sectores), [displays, filter, sectores]);
  const counts = useMemo(() => countDisplays(located), [located]);
  const visible = useMemo(() => located.filter((d) => displayMatchesStatus(d, filter.status)), [located, filter.status]);

  const locationLabel = (d: QueueDisplayScreen) => {
    const sector = findById(sectores, d.sectorId);
    const sede = findById(sedes, d.sedeId ?? getSectorSedeId(sector));
    return [sector?.name, sede?.name].filter(Boolean).join(' · ') || t('Offices.display.noSector');
  };

  const renderContent = () => {
    if (isLoading && displays.length === 0) return <ListSkeleton testId="displays-skeleton" />;
    if (displays.length === 0) {
      return (
        <EmptyState
          testId="displays-empty"
          icon={<Desktop24Regular />}
          title={t('Offices.display.emptyTitle')}
          description={t('Offices.display.emptyText')}
          actionLabel={t('Offices.display.create')}
          actionIcon={<Add16Regular />}
          onAction={onCreate}
        />
      );
    }
    if (visible.length === 0) {
      return <EmptyState icon={<Search20Regular />} title={t('Offices.noResultsTitle')} description={t('Offices.noResultsText')} testId="displays-no-results" />;
    }
    return (
      <div className={styles.surface}>
        <Table aria-label={t('Offices.tabs.displays')} className={styles.table}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell className={styles.headerCell}>{t('Offices.columns.display')}</TableHeaderCell>
              <TableHeaderCell className={styles.headerCell} style={{ width: '130px' }}>
                {t('Offices.columns.code')}
              </TableHeaderCell>
              <TableHeaderCell className={styles.headerCell}>{t('Offices.columns.location')}</TableHeaderCell>
              <TableHeaderCell className={mergeClasses(styles.headerCell, styles.numericHeader)} style={{ width: '130px' }}>
                {t('Offices.columns.rooms')}
              </TableHeaderCell>
              <TableHeaderCell className={styles.headerCell} style={{ width: '160px' }}>
                {t('Offices.columns.pairing')}
              </TableHeaderCell>
              <TableHeaderCell className={styles.headerCell} style={{ width: '130px' }}>
                {t('Offices.columns.status')}
              </TableHeaderCell>
              <TableHeaderCell className={styles.headerCell} style={{ width: '64px' }} aria-label={t('Offices.columns.actions')} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((d) => {
              const selected = selectedId !== undefined && d.id === selectedId;
              const medicoId = findTestCallMedicoId(d, consultorios);
              const rooms = getSectorRoomIds(findById(sectores, d.sectorId), consultorios).length;
              return (
                <TableRow
                  key={d.id ?? d.registrationNumber ?? ''}
                  className={mergeClasses(styles.row, selected && styles.rowSelected, !d.isActive && styles.rowInactive)}
                  aria-selected={selected}
                  data-testid={`display-row-${d.id}`}
                >
                  <TableCell>
                    <div className={styles.primaryCell}>
                      <Button appearance="transparent" className={styles.linkButton} onClick={() => onEdit(d)}>
                        {d.name || d.registrationNumber || `#${d.id}`}
                      </Button>
                      {d.description && <span className={styles.secondaryText}>{d.description}</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={styles.code}>{d.registrationNumber || '—'}</span>
                  </TableCell>
                  <TableCell>{locationLabel(d)}</TableCell>
                  <TableCell className={styles.numericCell}>{d.sectorId ? rooms : '—'}</TableCell>
                  <TableCell>
                    <DisplayPairingPill display={d} />
                  </TableCell>
                  <TableCell>
                    <DisplayActivePill display={d} />
                  </TableCell>
                  <TableCell>
                    <div className={styles.actionsCell}>
                      <Menu>
                        <MenuTrigger disableButtonEnhancement>
                          <Button
                            appearance="subtle"
                            icon={<MoreHorizontal20Regular />}
                            aria-label={t('Offices.actions.moreFor', { name: d.name || d.registrationNumber })}
                          />
                        </MenuTrigger>
                        <MenuPopover>
                          <MenuList>
                            <MenuItem icon={<Edit20Regular />} onClick={() => onEdit(d)}>
                              {t('Offices.actions.edit')}
                            </MenuItem>
                            <MenuItem
                              icon={<Speaker220Regular />}
                              disabled={!medicoId}
                              secondaryContent={!medicoId ? t('Offices.display.testCallUnavailable') : undefined}
                              onClick={() => medicoId && onTestCall(d, medicoId)}
                            >
                              {t('Offices.display.testCall')}
                            </MenuItem>
                            <MenuItem icon={<Delete20Regular />} className={styles.dangerItem} onClick={() => onDelete(d)}>
                              {t('Offices.actions.delete')}
                            </MenuItem>
                          </MenuList>
                        </MenuPopover>
                      </Menu>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <section className={styles.section} aria-label={t('Offices.tabs.displays')}>
      <div className={styles.toolbar}>
        <LocationFilters
          filter={filter}
          sedes={sedes}
          sectores={sectores}
          searchPlaceholder={t('Offices.display.searchPlaceholder')}
          onChange={(next) => onFilterChange({ ...filter, ...next })}
        />
        <div className={styles.toolbarActions}>
          <Button appearance="secondary" icon={<Link16Regular />} onClick={onPair}>
            {t('Offices.pairing.open')}
          </Button>
          <Button appearance="primary" icon={<Add16Regular />} onClick={onCreate}>
            {t('Offices.display.create')}
          </Button>
        </div>
      </div>
      <FilterPills<DisplayFilterKey>
        ariaLabel={t('Offices.filters.status')}
        selected={filter.status}
        onSelect={(status) => onFilterChange({ ...filter, status })}
        options={[
          { key: 'all', label: t('Offices.filters.all'), count: counts.all },
          { key: 'active', label: t('Offices.status.active'), count: counts.active, dot: 'success' },
          { key: 'inactive', label: t('Offices.status.inactive'), count: counts.inactive, dot: 'neutral' },
          { key: 'unpaired', label: t('Offices.status.unpaired'), count: counts.unpaired, dot: 'warning' },
        ]}
      />
      {renderContent()}
    </section>
  );
};

export default DisplaysTab;
